/**
 * Hard reload: clears SW caches, unregisters app SW, clears React Query, then reloads.
 * Preserves messaging SW (e.g. firebase-messaging-sw.js) and its caches.
 */
import type { QueryClient } from "@tanstack/react-query";

const PRESERVE_CACHE_PREFIXES = ["firebase-", "fcm-"];
const PRESERVE_SW_FILENAMES = ["firebase-messaging-sw.js"];

export async function hardReload(queryClient?: QueryClient): Promise<void> {
  try {
    // 1. Delete caches (skip messaging caches)
    if ("caches" in window) {
      const names = await caches.keys();
      await Promise.allSettled(
        names
          .filter((n) => !PRESERVE_CACHE_PREFIXES.some((p) => n.startsWith(p)))
          .map((n) => caches.delete(n)),
      );
    }

    // 2. Unregister app SW(s), keep messaging SW
    if ("serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.allSettled(
        regs
          .filter((r) => {
            const url = r.active?.scriptURL || r.waiting?.scriptURL || r.installing?.scriptURL || "";
            return !PRESERVE_SW_FILENAMES.some((f) => url.endsWith(f));
          })
          .map((r) => r.unregister()),
      );
    }

    // 3. Clear React Query
    queryClient?.clear();
  } catch (err) {
    console.warn("[hardReload] cleanup error:", err);
  } finally {
    // 4. Reload — cache-bust query so HTML revalidates
    const url = new URL(window.location.href);
    url.searchParams.set("_v", Date.now().toString(36));
    window.location.replace(url.toString());
  }
}
